/**
 * ICS Maker, the hosted site. It is a *host* for the component, not the home of any
 * event logic — everything below the page chrome comes from the shared packages.
 */

import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { eventCreatorTokensCss } from '@project/event-creator-web';

import { App } from './App';
import { DEFAULT_LANGUAGE, isLanguage, loadLanguage } from './i18n';
import { localizePage, pageForPath, pageTranslationKey } from './pages';
import './site.css';

// The page borrows the component's palette rather than keeping a second one: one definition,
// applied here at `:root` and by the component at `.ec-root`. Injected before the first render,
// so the page never paints with the fallback colours in site.css — unless the prerender already
// inlined it, in which case it is in the HTML before this script is even fetched.
const TOKENS_ID = 'ec-tokens';
if (document.getElementById(TOKENS_ID) === null) {
  const tokens = document.createElement('style');
  tokens.id = TOKENS_ID;
  tokens.textContent = eventCreatorTokensCss(':root');
  document.head.appendChild(tokens);
}

const container = document.getElementById('root');
if (container === null) throw new Error('index.html is missing #root');

// Which page this is comes from the URL, since each one is served as its own HTML file. No
// client-side router: the links are plain anchors, and on a static site a real navigation is
// both simpler and what a crawler follows anyway.
const basePath = import.meta.env.BASE_URL.replace(/^\/+|\/+$/g, '');
const pathParts = window.location.pathname
  .replace(/^\/+|\/+$/g, '')
  .split('/')
  .filter(Boolean);
if (basePath !== '' && pathParts[0] === basePath) pathParts.shift();
const candidateLanguage = pathParts[0] ?? '';
const language = isLanguage(candidateLanguage) ? candidateLanguage : DEFAULT_LANGUAGE;
if (language !== DEFAULT_LANGUAGE) pathParts.shift();
const strings = await loadLanguage(language);
const sourcePage = pageForPath(pathParts.join('/'));
const localizedPage = localizePage(sourcePage, strings.pages?.[pageTranslationKey(sourcePage)]);
document.documentElement.lang = language;

const page = (
  <StrictMode>
    <App page={localizedPage} language={language} strings={strings} />
  </StrictMode>
);

// Hydrate what the build rendered rather than throwing it away and re-rendering: same DOM, no
// flash, and React attaches to the markup a crawler already read. An empty #root means someone
// is serving the un-prerendered `index.html` (the dev server does), so fall back to mounting.
if (container.firstChild === null) createRoot(container).render(page);
else hydrateRoot(container, page);
